import React, { memo, useState } from 'react';
import { Popover } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import type { WorkOrderSort } from '../../../../../enums/workOrderSort';
import type { WorkOrderStatus } from '../../../../../enums/workOrderStatus';
import { SORT_OPTIONS, STATUS_LABELS } from '../../constants';
import styles from './StatusColumn.module.scss';

const FILTER_ICON = <span className={styles.columnSortIcon} aria-hidden />;
const CARET_ICON = <CaretDownOutlined aria-hidden />;

interface ColumnSortPopoverProps {
  status: WorkOrderStatus;
  sort: WorkOrderSort;
  onSortChange: (status: WorkOrderStatus, sort: WorkOrderSort) => void;
}

const ColumnSortPopover = ({ status, sort, onSortChange }: ColumnSortPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOptionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const nextSort = event.currentTarget.dataset.sort as WorkOrderSort;
    if (nextSort !== sort) onSortChange(status, nextSort);
    setIsOpen(false);
  };

  const content = (
    <div className={styles.sortPopover}>
      <p className={styles.sortPopoverTitle}>Sort by {CARET_ICON}</p>
      <ul className={styles.sortOptions}>
        {SORT_OPTIONS.map((option) => (
          <li key={option.value}>
            <button
              type="button"
              data-sort={option.value}
              aria-pressed={option.value === sort}
              className={`${styles.sortOption} ${option.value === sort ? styles.sortOptionActive : ''}`}
              onClick={handleOptionClick}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      destroyTooltipOnHide
      overlayClassName={styles.sortPopoverOverlay}
      content={content}
    >
      <button
        type="button"
        className={styles.columnSortButton}
        aria-label={`Sort ${STATUS_LABELS[status]} work orders`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {FILTER_ICON}
      </button>
    </Popover>
  );
};

export default memo(ColumnSortPopover);
